import Container from "@/components/ui/container"
import ErrorScreen from "@/components/ui/error-screen"
import { H1 } from "@/components/ui/headings"
import Panel from "@/components/ui/panel"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import LoadingScreen from "@/novus/components/loading-screen"
import { MainLayout } from "@/novus/layouts/MainLayout"
import { rootPath } from "@/novus/novus-paths"
import { useMutation, useQuery } from "@apollo/client"
import React from "react"
import { SubmitHandler } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import invariant from "tiny-invariant"
import DeductionForm, { DeductionFormValues } from "../components/DeductionForm"
import DeleteDeductionButton from "./components/DeleteDeductionButton"

const Deduction_Query = gql(`
  query EditDeduction($id: ID!) {
    deduction(id: $id) {
      ...ProfileDeductionFragment
    }
  }
`)

const UpdateDeduction_Mutation = gql(`
  mutation UpdateBeneificaryDeduction($input: UpdateDeductionInput!) {
    updateDeduction(input: $input) {
      deduction {
        ...ProfileDeductionFragment
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

const EditDeductionScreen = () => {
  const { id } = useParams()
  invariant(id, "expected id")
  const { toast } = useToast()
  const [mutate] = useMutation(UpdateDeduction_Mutation)
  const { data, loading, error } = useQuery(Deduction_Query, {
    variables: { id },
    skip: !id,
  })
  const navigate = useNavigate()
  if (loading) return <LoadingScreen />
  if (error || !data?.deduction) return <ErrorScreen message={error?.message} />

  const deduction = data?.deduction

  const defaultValues: DeductionFormValues = {
    fiveTwoNineAccountId: deduction?.fiveTwoNineAccount.id || "",
    amount: deduction.amountCents / 100,
  }

  const onSubmit: SubmitHandler<DeductionFormValues> = async (values) => {
    const { data } = await mutate({
      variables: {
        input: {
          id,
          fiveTwoNineAccountId: values.fiveTwoNineAccountId,
          amountCents: Number(values.amount) * 100,
        },
      },
    })

    if (
      data?.updateDeduction.errors &&
      data?.updateDeduction.errors.length > 0
    ) {
      throw data?.updateDeduction.errors
    }
    toast({
      title: "Deduction has been updated.",
    })

    navigate(rootPath.pattern)
  }

  return (
    <MainLayout>
      <Container>
        <Panel backPath={rootPath.pattern} minHeight={true}>
          <div className="text-center mb-14">
            <H1>Edit Deduction</H1>
          </div>
          <DeductionForm onSubmit={onSubmit} defaultValues={defaultValues} />
        </Panel>
        <div className="flex items-center justify-center mt-8">
          <DeleteDeductionButton id={id} />
        </div>
      </Container>
    </MainLayout>
  )
}

export default EditDeductionScreen
